import {
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  computed,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SectionAccordionComponent } from '../../../shared/components/section-accordion/section-accordion.component';
import { CustomDatePickerComponent } from '../../../shared/components/custom-date-picker/custom-date-picker.component';
import { FleetService } from '../../services/fleet.service';
import { NotificationService } from '../../../services/notification.service';
import { FleetInvoiceRecord } from '../../models/fleet.model';
import {
  formatCalendarDateInTimeZone,
  formatDateToISO,
  getUserTimezone,
} from '../../../shared/utils/date.util';
import {
  currentOpenPeriod,
  formatBillingCycleRange,
  lastCompletedPeriod,
} from '../../utils/fleet-billing-cycle.util';

export interface InvoiceModalData {
  fleetId: string;
  fleetName: string;
  totalVehicles: number;
  pricePerVehicle: number;
  subtotal: number;
  tax: number;
  grandTotal: number;
  /** Raw billing cycle type (weekly|biweekly|monthly|custom). */
  billingCycleType: string;
  /** Cycle anchor YYYY-MM-DD (falls back to start date). */
  billingCycleAnchor: string;
}

export type InvoicePeriod = 'current' | 'last' | 'custom';

export interface PastInvoice {
  id: string;
  invoiceNumber: string;
  billingCycle: string;
}

const PERIOD_LABELS: Record<InvoicePeriod, string> = {
  current: 'Current Billing cycle',
  last: 'Last Billing cycle',
  custom: 'Custom range',
};

/**
 * Invoice modal for generating a fleet invoice — chooses a billing period, lists past
 * invoices, creates via POST /fleet/invoice/:id, and downloads the signed PDF.
 */
@Component({
  selector: 'app-invoice-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, SectionAccordionComponent, CustomDatePickerComponent],
  templateUrl: './invoice-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./invoice-modal.component.scss'],
})
export class InvoiceModalComponent implements OnChanges {
  private readonly fleetService = inject(FleetService);
  private readonly messageService = inject(NotificationService);

  @Input() isVisible = false;
  @Input() data: InvoiceModalData | null = null;
  @Output() close = new EventEmitter<void>();
  /** Emitted after a successful PDF download so the parent can close the modal. */
  @Output() generate = new EventEmitter<void>();

  readonly pastInvoices = signal<PastInvoice[]>([]);
  readonly pastInvoicesLoading = signal(false);
  readonly generating = signal(false);
  readonly downloading = signal(false);

  readonly period = signal<InvoicePeriod>('current');
  readonly customStartDate = signal<Date | null>(null);
  readonly customEndDate = signal<Date | null>(null);
  /** Invoice periods are historical, so the custom range can't reach into the future. */
  readonly maxCustomRangeDate = new Date();
  readonly showPastInvoices = signal(false);
  readonly generatedInvoice = signal<FleetInvoiceRecord | null>(null);
  readonly resolvedPeriodLabel = signal('');

  readonly periodLabel = computed(() => this.resolvedPeriodLabel() || PERIOD_LABELS[this.period()]);

  readonly isGenerateDisabled = computed(() => {
    if (this.generating()) return true;
    if (this.period() === 'custom') {
      return !this.customStartDate() || !this.customEndDate();
    }
    return this.resolveSelectedPeriod() == null;
  });

  /** Monotonic id so overlapping getInvoices responses cannot overwrite newer state. */
  private pastInvoicesRequestId = 0;

  ngOnChanges(changes: SimpleChanges): void {
    const dataChanged = Boolean(changes['data'] && this.data);
    const becameVisible = Boolean(changes['isVisible'] && this.isVisible && this.data);
    if (!dataChanged && !becameVisible) {
      return;
    }
    if (dataChanged) {
      this.resetLocalState();
    }
    // One load when data and visibility change together (avoids duplicate racing requests).
    this.loadPastInvoices();
  }

  private resetLocalState(): void {
    this.period.set('current');
    this.customStartDate.set(null);
    this.customEndDate.set(null);
    this.showPastInvoices.set(false);
    this.generatedInvoice.set(null);
    this.resolvedPeriodLabel.set('');
    this.generating.set(false);
    this.downloading.set(false);
  }

  private loadPastInvoices(): void {
    const fleetId = this.data?.fleetId;
    if (!fleetId) return;

    const requestId = (this.pastInvoicesRequestId += 1);
    this.pastInvoicesLoading.set(true);
    this.fleetService.getInvoices(fleetId).subscribe({
      next: rows => {
        if (requestId !== this.pastInvoicesRequestId) return;
        this.pastInvoices.set(
          rows.map(row => ({
            id: row.id,
            invoiceNumber: row.invoice_number,
            billingCycle: formatBillingCycleRange(
              String(row.invoice_period_start).slice(0, 10),
              String(row.invoice_period_end).slice(0, 10)
            ),
          }))
        );
        this.pastInvoicesLoading.set(false);
      },
      error: error => {
        if (requestId !== this.pastInvoicesRequestId) return;
        this.pastInvoices.set([]);
        this.pastInvoicesLoading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Unable to Load Invoices',
          detail: error.error?.message || error.message || 'Failed to load past invoices.',
          life: 4000,
        });
      },
    });
  }

  private todayIso(): string {
    const tz = getUserTimezone() || 'UTC';
    return formatCalendarDateInTimeZone(new Date(), tz) || formatDateToISO(new Date());
  }

  private resolveSelectedPeriod(): { periodStart: string; periodEnd: string } | null {
    const data = this.data;
    if (!data?.billingCycleAnchor) {
      if (this.period() === 'custom') {
        return this.resolveCustomPeriod();
      }
      return null;
    }

    const asOfDate = this.todayIso();
    const cycleArgs = {
      billingCycleType: data.billingCycleType || 'monthly',
      billingCycleAnchor: data.billingCycleAnchor,
      asOfDate,
    };

    if (this.period() === 'current') {
      return currentOpenPeriod(cycleArgs);
    }
    if (this.period() === 'last') {
      return lastCompletedPeriod(cycleArgs);
    }
    return this.resolveCustomPeriod();
  }

  private resolveCustomPeriod(): { periodStart: string; periodEnd: string } | null {
    const start = this.customStartDate();
    const end = this.customEndDate();
    if (!start || !end) return null;
    const periodStart = formatDateToISO(start);
    const periodEnd = formatDateToISO(end);
    if (!periodStart || !periodEnd || periodEnd < periodStart) return null;
    return { periodStart, periodEnd };
  }

  onPeriodSelect(period: InvoicePeriod): void {
    this.period.set(period);
    this.generatedInvoice.set(null);
    this.resolvedPeriodLabel.set('');
  }

  onCustomStartDateChange(date: Date): void {
    this.customStartDate.set(date);
  }

  onCustomEndDateChange(date: Date): void {
    this.customEndDate.set(date);
  }

  togglePastInvoices(): void {
    this.showPastInvoices.update(show => !show);
  }

  onDownloadPastInvoice(invoice: PastInvoice): void {
    const fleetId = this.data?.fleetId;
    if (!fleetId || !invoice.id || this.downloading()) return;

    this.downloading.set(true);
    this.fleetService.getInvoicePdf(fleetId, invoice.id).subscribe({
      next: row => {
        this.downloading.set(false);
        this.openPdfUrl(row);
      },
      error: error => {
        this.downloading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Download Failed',
          detail: error.error?.message || error.message || 'Failed to download invoice PDF.',
          life: 4000,
        });
      },
    });
  }

  onGenerate(): void {
    if (this.isGenerateDisabled() || !this.data?.fleetId) {
      return;
    }

    const period = this.resolveSelectedPeriod();
    if (!period) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Billing Period',
        detail:
          this.period() === 'last'
            ? 'No completed billing cycle is available yet for this fleet.'
            : 'Unable to resolve the selected billing period.',
        life: 4000,
      });
      return;
    }

    this.generating.set(true);
    this.resolvedPeriodLabel.set(formatBillingCycleRange(period.periodStart, period.periodEnd));

    this.fleetService
      .createInvoice(this.data.fleetId, {
        period_start: period.periodStart,
        period_end: period.periodEnd,
        timezone: getUserTimezone() || undefined,
      })
      .subscribe({
        next: invoice => {
          this.generating.set(false);
          this.generatedInvoice.set(invoice);
          this.loadPastInvoices();
          if (invoice.pdf_status === 'failed' || !invoice.pdf_url) {
            this.messageService.add({
              severity: 'warn',
              summary: 'Invoice Created',
              detail: 'Invoice was saved but the PDF is not ready yet. Try Download pdf again.',
              life: 5000,
            });
          }
        },
        error: error => {
          this.generating.set(false);
          this.generatedInvoice.set(null);
          this.messageService.add({
            severity: 'error',
            summary: 'Generate Failed',
            detail: error.error?.message || error.message || 'Failed to generate invoice.',
            life: 4000,
          });
        },
      });
  }

  onDownloadPdf(): void {
    const fleetId = this.data?.fleetId;
    const invoice = this.generatedInvoice();
    if (!fleetId || !invoice?.id || this.downloading()) return;

    if (invoice.pdf_url && invoice.pdf_status !== 'failed') {
      this.openPdfUrl(invoice);
      this.generate.emit();
      return;
    }

    this.downloading.set(true);
    this.fleetService.getInvoicePdf(fleetId, invoice.id).subscribe({
      next: row => {
        this.downloading.set(false);
        this.generatedInvoice.set(row);
        this.openPdfUrl(row);
        this.generate.emit();
      },
      error: error => {
        this.downloading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Download Failed',
          detail: error.error?.message || error.message || 'Failed to download invoice PDF.',
          life: 4000,
        });
      },
    });
  }

  private openPdfUrl(row: FleetInvoiceRecord): void {
    if (!row.pdf_url) {
      this.messageService.add({
        severity: 'error',
        summary: 'PDF Unavailable',
        detail: 'No PDF URL was returned for this invoice.',
        life: 4000,
      });
      return;
    }
    window.open(row.pdf_url, '_blank', 'noopener,noreferrer');
  }

  onClose(): void {
    this.close.emit();
  }
}
